import './ActionTargetRow.css';

import React, { useRef } from 'react';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MoreVertical16Regular, Open16Regular } from '@fluentui/react-icons';

import { ActionStatus, ActionTargetTimelineEvent, ActionTransferProgress } from '../../types/actionTypes';
import { formatActionDateTime } from '../../utils/actionPresentation';
import { formatDuration } from '../../utils/formatDuration';
import ActionStatusIcon from './ActionStatusIcon';
import ActionTransferProgressView from '../../features/actions/ActionTransferProgressView';

export interface ActionTargetRowMenuAction {
  key: string;
  label: string;
  icon?: string;
  danger?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onSelect: () => void;
}

export interface ActionTargetRowProps {
  status: ActionStatus | null | undefined;
  name: string;
  /** Technical identity (e.g. full RTU serial) shown only as a hover tooltip - never the visible name. */
  nameTooltip?: string;
  ip?: string;
  phase?: string;
  durationMs?: number;
  responseMessage?: string;
  transfer?: ActionTransferProgress;
  timeline?: ActionTargetTimelineEvent[];
  menuActions?: ActionTargetRowMenuAction[];
  /** Full-page navigation (renders an anchor). Mutually exclusive with onActivate. */
  activityHref?: string;
  /** In-app router navigation via a click handler (renders a button). Mutually exclusive with activityHref. */
  onActivate?: () => void;
  /** Present only when a downloadable artifact exists for this target's transfer. */
  onDownloadArtifact?: () => void;
  downloadLabel?: string;
  downloadingArtifact?: boolean;
  /** Name of the file produced by this target's transfer, shown once known. */
  artifactFileName?: string;
  compact?: boolean;
}

/**
 * One target's status row: badge, name/IP/phase, right-aligned duration, and
 * an optional overflow menu / disclosure timeline. Shared by the workbench
 * detail panel and the tracker's expanded card so a target looks and behaves
 * the same wherever it appears.
 */
const ActionTargetRow: React.FC<ActionTargetRowProps> = ({
  status,
  name,
  nameTooltip,
  ip,
  phase,
  durationMs,
  responseMessage,
  transfer,
  timeline,
  menuActions,
  activityHref,
  onActivate,
  onDownloadArtifact,
  downloadLabel,
  downloadingArtifact = false,
  artifactFileName,
  compact = false,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<Menu>(null);

  const menuItems: MenuItem[] | undefined = menuActions?.map((action) => ({
    label: action.label,
    icon: action.icon,
    disabled: action.disabled || action.loading,
    className: action.danger ? 'action-target-row__menu-item--danger' : undefined,
    command: action.onSelect,
  }));

  return (
    <div className={`action-target-row${compact ? ' action-target-row--compact' : ''}`}>
      <ActionStatusIcon status={status} />
      <div className="action-target-row__main">
        <div className="action-target-row__top">
          <strong className="action-target-row__name" title={nameTooltip}>{name}</strong>
          {ip ? <span className="action-target-row__ip">{ip}</span> : null}
          {phase ? <span className="action-target-row__phase">{phase}</span> : null}
        </div>
        {responseMessage ? <small className="action-target-row__message">{responseMessage}</small> : null}
        <ActionTransferProgressView
          compact
          transfer={transfer}
          onDownload={onDownloadArtifact}
          downloadLabel={downloadLabel}
          downloading={downloadingArtifact}
          fileName={artifactFileName}
        />
        {timeline && timeline.length > 0 ? (
          <details className="action-target-row__timeline">
            <summary>{t('actionsWorkbench.executionTimeline', { defaultValue: 'Execution timeline ({{count}})', count: timeline.length })}</summary>
            {timeline.map((event, index) => (
              <div className="action-target-row__timeline-event" key={event.id ?? index}>
                <span className="action-target-row__timeline-heading">
                  <time>{formatActionDateTime(event.occurredAtUtc || event.occurredAt, t('actionsWorkbench.timeNotReported', { defaultValue: 'Time not reported' }))}</time>
                  <strong>{event.phaseLabel || event.phaseCode || event.status || t('actionsWorkbench.update', { defaultValue: 'Update' })}</strong>
                </span>
                {event.message ? <small>{event.message}</small> : null}
              </div>
            ))}
          </details>
        ) : null}
      </div>
      <div className="action-target-row__end">
        {durationMs !== undefined ? <span className="action-target-row__duration">{formatDuration(durationMs)}</span> : null}
        {activityHref ? (
          <Link
            className="action-target-row__activity"
            to={activityHref}
            aria-label={t('actionsWorkbench.rtuActivityNamed', { defaultValue: '{{name}} activity', name })}
            title={t('actionsWorkbench.rtuActivity', { defaultValue: 'RTU activity' })}
          >
            <Open16Regular aria-hidden="true" />
          </Link>
        ) : null}
        {!activityHref && onActivate ? (
          <button
            type="button"
            className="action-target-row__activity action-target-row__activity--button"
            onClick={onActivate}
            aria-label={t('actionsWorkbench.rtuActivityNamed', { defaultValue: '{{name}} activity', name })}
            title={t('actionsWorkbench.rtuActivity', { defaultValue: 'RTU activity' })}
          >
            <Open16Regular aria-hidden="true" />
          </button>
        ) : null}
        {menuItems && menuItems.length > 0 ? (
          <>
            <button
              type="button"
              className="action-target-row__menu-trigger"
              aria-label={t('actionsWorkbench.targetActions', { defaultValue: 'Target actions' })}
              onClick={(event) => menuRef.current?.toggle(event)}
            >
              <MoreVertical16Regular aria-hidden="true" />
            </button>
            <Menu model={menuItems} popup ref={menuRef} popupAlignment="right" />
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ActionTargetRow;
